import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaStar, FaRegStar, FaHeart, FaRegHeart, FaPlus, FaMinus, FaShoppingCart, FaUser } from 'react-icons/fa';
import { RatingStarsFa, getDisplayRating } from '../utils/ratingStars';
import { Loader2, MessageCircle, Send } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useFavoriteAuth } from '../hooks/useFavoriteAuth';
import { getDishSupplements } from '../services/supplementApi';
import { IMAGE_CLASSES } from '../constants/imageSizes';
import { getDishComments, createDishComment } from '../services/api';
import namkeFallback from '../assets/namke-fallback.png';

const normalizeComments = (payload) => {
  const rows = Array.isArray(payload) ? payload : [];
  return rows.filter((c) => c && typeof c === 'object');
};

const computeAverageRating = (rows) => {
  const valid = rows
    .map((c) => Number(c?.rating))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (!valid.length) return 0;
  return valid.reduce((sum, n) => sum + n, 0) / valid.length;
};

const DATE_LOCALES = { fr: 'fr-FR', en: 'en-GB', de: 'de-DE' };

const DetailCard = ({ dish }) => {
  const { t, i18n } = useTranslation();
  const { addToCart, updateCartItem } = useCart();
  
  const [quantity, setQuantity] = useState(1);
  const [selectedSupplements, setSelectedSupplements] = useState([]);
  const [supplements, setSupplements] = useState([]);
  const [supplementsLoading, setSupplementsLoading] = useState(true);
  const [showAddToCartNotification, setShowAddToCartNotification] = useState(false);
  const [isEditingCartItem, setIsEditingCartItem] = useState(false);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPos, setZoomPos] = useState({ x: 50, y: 50 });

  // Hook d'authentification pour les favoris
  const {
    isAuthenticated,
    showLoginPopup,
    loading: favoriteLoading,
    handleFavoriteAction,
    handleLoginSuccess,
    closeLoginPopup,
    checkFavoriteStatus
  } = useFavoriteAuth();

  const [isFavorite, setIsFavorite] = useState(false);
  
  // États pour les commentaires et la notation
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [newRating, setNewRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);

  // Vérifier si on est en mode édition d'un article du panier
  useEffect(() => {
    const editData = localStorage.getItem('editCartItem');
    if (editData && dish?.id) {
      try {
        const parsedData = JSON.parse(editData);
        if (parsedData.itemId === dish.id && parsedData.returnToCart) {
          setIsEditingCartItem(true);
          setQuantity(parsedData.currentQuantity);
          setSelectedSupplements(parsedData.currentSupplements);
          
          // Nettoyer le localStorage après utilisation
          localStorage.removeItem('editCartItem');
        }
      } catch (error) {
        console.error('Erreur lors du parsing des données d\'édition:', error);
        localStorage.removeItem('editCartItem');
      }
    }
  }, [dish?.id]);

  // Charger les suppléments
  useEffect(() => {
    const fetchSupplements = async () => {
      if (!dish?.id) return;
      
      try {
        setSupplementsLoading(true);
        const supplementsData = await getDishSupplements(dish.id);
        setSupplements(supplementsData);
      } catch (error) {
        console.error('Erreur lors du chargement des suppléments:', error);
        setSupplements([]);
      } finally {
        setSupplementsLoading(false);
      }
    };

    fetchSupplements();
  }, [dish?.id]);

  // Vérifier le statut des favoris
  useEffect(() => {
    const checkStatus = async () => {
      if (!isAuthenticated || !dish?.id) {
        setIsFavorite(false);
        return;
      }

      try {
        const status = await checkFavoriteStatus(dish.id, 'dish');
        setIsFavorite(status);
      } catch (error) {
        console.error('Error checking favorite status:', error);
        setIsFavorite(false);
      }
    };

    checkStatus();
  }, [dish?.id, isAuthenticated, checkFavoriteStatus]);
  
  // Charger les commentaires
  useEffect(() => {
    const fetchComments = async () => {
      if (!dish?.id) return;
      
      try {
        setCommentsLoading(true);
        const response = await getDishComments(dish.id);
        setComments(normalizeComments(response));
      } catch (error) {
        console.error('Erreur lors du chargement des commentaires:', error);
        setComments([]);
      } finally {
        setCommentsLoading(false);
      }
    };

    fetchComments();
  }, [dish?.id]);
  
  // Soumettre un commentaire
  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      alert(t('productDetails.mustBeLoggedInForReview'));
      return;
    }

    if (!newComment.trim()) {
      alert(t('productDetails.pleaseWriteComment'));
      return;
    }

    try {
      setIsSubmittingComment(true);

      if (!localStorage.getItem('access_token')) {
        alert(t('productDetails.mustBeLoggedInForReview'));
        setIsSubmittingComment(false);
        return;
      }

      const response = await createDishComment({
        dish_id: dish.id,
        rating: newRating,
        content: newComment.trim(),
      });
      
      // L'API peut renvoyer l'objet direct (pas forcément response.data)
      const createdComment = response?.data ?? response;
      if (createdComment && typeof createdComment === 'object') {
        setComments((prev) => {
          const next = [createdComment, ...prev].filter((c) => c && typeof c === 'object');
          const nextAvg = computeAverageRating(next);
          window.dispatchEvent(
            new CustomEvent('afrodine:dish-review-updated', {
              detail: {
                dishId: dish.id,
                rating: nextAvg,
                reviewCount: next.length,
              },
            })
          );
          return next;
        });
      }
      
      // Réinitialiser le formulaire
      setNewComment('');
      setNewRating(5);
      setShowCommentForm(false);
      
      // Afficher une notification de succès
      alert(t('productDetails.reviewAddedSuccess'));
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error);

      // Message d'erreur plus spécifique selon le type d'erreur (comparaison sur le message brut du backend, non traduit)
      if (error?.message?.toLowerCase().includes('token') || error?.message?.includes('401')) {
        alert(t('productDetails.sessionExpired'));
        // Rediriger vers la page de connexion
        window.location.href = '/login';
      } else if (error?.message?.includes('déjà laissé')) {
        alert(error.message || t('productDetails.alreadyReviewed'));
      } else {
        alert(t('productDetails.reviewAddError'));
      }
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Gérer le toggle des favoris
  const handleFavoriteToggle = async () => {
    if (!dish?.id) return;
    
    const action = isFavorite ? 'remove' : 'add';
    const success = await handleFavoriteAction(action, dish.id, 'dish');
    
    if (success) {
      setIsFavorite(!isFavorite);
    }
  };

  // Gérer la sélection des suppléments
  const handleSupplementToggle = (supplement) => {
    const isSelected = selectedSupplements.some(s => s.id === supplement.id);
    
    if (isSelected) {
      setSelectedSupplements(prev => prev.filter(s => s.id !== supplement.id));
    } else {
      setSelectedSupplements(prev => [...prev, {
        id: supplement.id,
        name: supplement.name,
        price: supplement.price,
        quantity: 1
      }]);
    }
  };

  // Gérer la quantité des suppléments
  const handleSupplementQuantityChange = (supplementId, newQuantity) => {
    if (newQuantity >= 1) {
      setSelectedSupplements(prev => prev.map(s => 
        s.id === supplementId 
          ? { ...s, quantity: newQuantity }
          : s
      ));
    } else if (newQuantity === 0) {
      setSelectedSupplements(prev => prev.filter(s => s.id !== supplementId));
    }
  };

  // Calculer le prix total
  const calculateTotalPrice = () => {
    const basePrice = dish?.price || 0;
    const supplementsPrice = selectedSupplements.reduce((total, sup) => 
      total + (sup.price * sup.quantity), 0
    );
    return (basePrice + supplementsPrice) * quantity;
  };

  // Ajouter au panier
  const handleAddToCart = () => {
    if (!dish) return;

    const cartItem = {
      id: dish.id,
      name: dish.name,
      price: dish.price,
      image: dish.dish_image_url || dish.image,
      restaurantId: dish.restaurant_id || dish.restaurantId,
      restaurantName: dish.restaurant?.name || dish.restaurant,
      quantity: quantity,
      supplements: selectedSupplements
    };

    if (isEditingCartItem) {
      // Mettre à jour l'article existant dans le panier
      updateCartItem(dish.id, cartItem);
      
      // Afficher la notification de mise à jour
      setShowAddToCartNotification(true);
      setTimeout(() => setShowAddToCartNotification(false), 3000);
      
      // Rediriger vers le panier
      setTimeout(() => {
        window.location.href = '/cart';
      }, 1500);
    } else {
      // Ajouter un nouvel article au panier
      addToCart(cartItem);
      
      // Afficher la notification
      setShowAddToCartNotification(true);
      setTimeout(() => setShowAddToCartNotification(false), 3000);
    }
  };

  const renderStars = (rating) => <RatingStarsFa rating={rating} size="lg" />;
  const commentsAverageRating = computeAverageRating(comments);
  const displayRating = comments.length > 0 ? commentsAverageRating : getDisplayRating(dish, 0);
  const displayReviewCount = comments.length > 0 ? comments.length : (dish.review_count || dish.reviewCount || 0);

  if (!dish) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
      </div>
    );
  }

  const cardStyle = { background: '#FCFBF5', borderRadius: '24px', border: '1px solid rgba(5,16,4,0.1)', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '12px' };
  const pillStyle = { fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#FBF9D9', padding: '6px 13px', borderRadius: '999px' };

  return (
    <>
      {showAddToCartNotification && (
        <div style={{ position: 'fixed', top: '80px', right: '16px', zIndex: 50, background: '#054625', color: '#FBF9D9', padding: '14px 22px', borderRadius: '999px', boxShadow: '0 10px 24px rgba(5,16,4,0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
          <FaShoppingCart className="w-5 h-5" />
          <span>{isEditingCartItem ? t('productDetails.cartItemUpdated') : t('productDetails.dishAddedToCart')}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 'clamp(24px, 4vw, 40px)', alignItems: 'start' }}>
        {/* Colonne image + histoire */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
          <div
            style={{ borderRadius: 'clamp(28px, 3vw, 36px)', overflow: 'hidden', boxShadow: '0 12px 30px rgba(5,16,4,0.1)', position: 'relative', cursor: 'zoom-in' }}
            onMouseEnter={() => setIsZooming(true)}
            onMouseLeave={() => setIsZooming(false)}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setZoomPos({ x: ((e.clientX - rect.left) / rect.width) * 100, y: ((e.clientY - rect.top) / rect.height) * 100 });
            }}
          >
            <img
              src={dish.dish_image_url || dish.image || namkeFallback}
              alt={dish.name}
              style={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', transform: isZooming ? 'scale(2)' : 'scale(1)', transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`, transition: 'transform 0.2s ease' }}
              onError={(e) => { e.target.src = namkeFallback; }}
            />
            {(dish.discount_percentage || dish.is_promoted) && (
              <div style={{ position: 'absolute', top: '14px', left: '14px', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '6px 13px', borderRadius: '999px' }}>
                {dish.discount_percentage ? `-${dish.discount_percentage}%` : t('productDetails.promo')}
              </div>
            )}
            <button
              onClick={handleFavoriteToggle}
              disabled={favoriteLoading}
              style={{ position: 'absolute', top: '14px', right: '14px', border: 'none', cursor: favoriteLoading ? 'not-allowed' : 'pointer', width: '42px', height: '42px', borderRadius: '999px', display: 'grid', placeItems: 'center', background: isFavorite ? '#EF4C23' : '#FCFBF5', color: isFavorite ? '#FCFBF5' : '#051004', opacity: favoriteLoading ? 0.6 : 1 }}
            >
              {favoriteLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isFavorite ? <FaHeart className="w-4 h-4" /> : <FaRegHeart className="w-4 h-4" />}
            </button>
          </div>

          <div style={{ ...cardStyle, marginTop: '4px' }}>
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '24px', letterSpacing: '-0.02em' }}>{t('productDetails.dishStory')}</h2>
            <p style={{ margin: 0, fontSize: '15.5px', lineHeight: 1.65, color: 'rgba(5,16,4,0.8)' }}>{dish.description}</p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '4px' }}>
              {dish.category?.name && <span style={pillStyle}>{dish.category.name}</span>}
              {dish.diet_type && <span style={{ ...pillStyle, textTransform: 'capitalize' }}>{dish.diet_type}</span>}
              <span style={{ ...pillStyle, background: dish.is_available ? 'rgba(114,183,68,0.18)' : 'rgba(239,76,35,0.14)', color: dish.is_available ? '#054625' : '#EF4C23', fontWeight: 600 }}>
                {dish.is_available ? t('productDetails.available') : t('productDetails.unavailable')}
              </span>
            </div>
          </div>
        </div>

        {/* Colonne commande */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(30px, 4vw, 44px)', letterSpacing: '-0.02em', lineHeight: 1 }}>{dish.name}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'Archivo, sans-serif', fontSize: '14.5px' }}>
              {renderStars(displayRating)}
              <span style={{ fontWeight: 600, color: '#054625' }}>{displayRating > 0 ? displayRating.toFixed(1) : '—'}</span>
              <span style={{ color: 'rgba(5,16,4,0.66)' }}>{t('productDetails.reviewCountParens', { count: displayReviewCount })}</span>
            </div>
          </div>

          <div style={{ ...cardStyle, gap: '22px', boxShadow: '0 10px 26px rgba(5,16,4,0.07)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '32px', letterSpacing: '-0.02em' }}>{dish.price?.toFixed(2) || '0.00'} €</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{t('productDetails.quantity')}</span>
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', background: '#FBF9D9', borderRadius: '999px', padding: '5px' }}>
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} style={{ border: 'none', cursor: 'pointer', width: '34px', height: '34px', borderRadius: '999px', background: '#FCFBF5', color: '#054625', display: 'grid', placeItems: 'center' }}>
                  <FaMinus className="w-3 h-3" />
                </button>
                <span style={{ minWidth: '26px', textAlign: 'center', fontWeight: 600, fontSize: '16px' }}>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} style={{ border: 'none', cursor: 'pointer', width: '34px', height: '34px', borderRadius: '999px', background: '#054625', color: '#FBF9D9', display: 'grid', placeItems: 'center' }}>
                  <FaPlus className="w-3 h-3" />
                </button>
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{t('productDetails.supplements')}</span>
              {supplementsLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', fontSize: '14px', color: 'rgba(5,16,4,0.66)' }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('productDetails.loadingSupplements')}
                </div>
              ) : supplements.length > 0 ? (
                supplements.map((supplement) => {
                  const isSelected = selectedSupplements.some(s => s.id === supplement.id);
                  const selectedSupplement = selectedSupplements.find(s => s.id === supplement.id);
                  return (
                    <div key={supplement.id} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', fontFamily: 'Poppins, sans-serif', fontSize: '14.5px', padding: '13px 18px', borderRadius: '20px', background: isSelected ? '#FBF9D9' : 'transparent', border: `1px solid ${isSelected ? '#054625' : 'rgba(5,16,4,0.14)'}` }}>
                      <label htmlFor={`supplement-${supplement.id}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input id={`supplement-${supplement.id}`} type="checkbox" checked={isSelected} onChange={() => handleSupplementToggle(supplement)} style={{ width: '17px', height: '17px', accentColor: '#054625' }} />
                        {supplement.name}
                      </label>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isSelected && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <button onClick={() => handleSupplementQuantityChange(supplement.id, selectedSupplement.quantity - 1)} style={{ border: 'none', cursor: 'pointer', width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', display: 'grid', placeItems: 'center', fontSize: '11px' }}><FaMinus className="h-2 w-2" /></button>
                            <span style={{ fontSize: '13px', fontWeight: 600, width: '16px', textAlign: 'center' }}>{selectedSupplement.quantity}</span>
                            <button onClick={() => handleSupplementQuantityChange(supplement.id, selectedSupplement.quantity + 1)} style={{ border: 'none', cursor: 'pointer', width: '22px', height: '22px', borderRadius: '999px', background: '#FCFBF5', display: 'grid', placeItems: 'center', fontSize: '11px' }}><FaPlus className="h-2 w-2" /></button>
                          </span>
                        )}
                        <span style={{ fontWeight: 600 }}>+{supplement.price?.toFixed(2) || '0.00'} €</span>
                      </span>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '16px', borderRadius: '20px', background: '#FBF9D9', fontSize: '13.5px', color: 'rgba(5,16,4,0.66)' }}>{t('productDetails.noSupplementsAvailable')}</div>
              )}
            </div>

            <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14px', color: 'rgba(5,16,4,0.7)' }}>{t('productDetails.total')}</span>
                <strong style={{ marginLeft: 'auto', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{calculateTotalPrice().toFixed(2)} €</strong>
              </span>
              <button
                onClick={handleAddToCart}
                style={{ border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', padding: '17px 28px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
              >
                <FaShoppingCart className="w-4 h-4" />
                {isEditingCartItem ? t('productDetails.updateInCart') : t('productDetails.addToCart')}
              </button>
            </div>
          </div>

          {dish.restaurant?.name && (
            <div style={{ ...cardStyle, background: '#054625', color: '#FBF9D9', border: 'none' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>{t('productDetails.restaurantLabel')}</span>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                {dish.restaurant_id ? (
                  <Link to={`/restaurant/${dish.restaurant_id}`} style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px', color: '#FBF9D9' }}>{dish.restaurant.name}</Link>
                ) : (
                  <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>{dish.restaurant.name}</strong>
                )}
                {dish.restaurant.is_open != null && (
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', fontWeight: 600, padding: '6px 13px', borderRadius: '999px', background: dish.restaurant.is_open ? '#72B744' : 'rgba(251,249,217,0.16)', color: dish.restaurant.is_open ? '#051004' : '#FBF9D9' }}>
                    {dish.restaurant.is_open ? t('productDetails.open') : t('productDetails.closed')}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px' }}>
                <RatingStarsFa rating={dish.restaurant.rating || 0} size="sm" />
                <span style={{ fontWeight: 600 }}>{(dish.restaurant.rating || 0).toFixed(1)}</span>
                {dish.restaurant.review_count ? <span style={{ color: 'rgba(251,249,217,0.7)' }}>{t('productDetails.reviewCountParens', { count: dish.restaurant.review_count })}</span> : null}
              </div>
              {(dish.restaurant.cuisine_type || dish.restaurant.address || dish.restaurant.city) && (
                <span style={{ fontSize: '14px', lineHeight: 1.55, color: 'rgba(251,249,217,0.82)' }}>
                  {[dish.restaurant.cuisine_type, [dish.restaurant.address, dish.restaurant.city].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
                </span>
              )}
              {dish.restaurant_id && (
                <Link to={`/restaurant/${dish.restaurant_id}`} style={{ alignSelf: 'flex-start', fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, color: '#FFC717' }}>{t('productDetails.viewRestaurant')}</Link>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Avis */}
      <section style={{ marginTop: 'clamp(40px, 5vw, 72px)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(24px, 3vw, 34px)', letterSpacing: '-0.02em' }}>{t('productDetails.reviewsTitle')}</h2>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14.5px', fontWeight: 600, color: '#054625' }}>{t('productDetails.reviewCount', { count: comments.length })}</span>
          {isAuthenticated && !showCommentForm && (
            <button onClick={() => setShowCommentForm(true)} style={{ marginLeft: 'auto', border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 22px', borderRadius: '999px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaStar size={14} /> {t('productDetails.leaveReview')}
            </button>
          )}
          {!isAuthenticated && (
            <span style={{ marginLeft: 'auto', fontSize: '14px', color: 'rgba(5,16,4,0.7)' }}>
              <a href="/login" style={{ color: '#EF4C23', fontWeight: 600 }}>{t('productDetails.logIn')}</a> {t('productDetails.toLeaveReview')}
            </span>
          )}
        </div>

        {isAuthenticated && showCommentForm && (
          <form onSubmit={handleSubmitComment} style={{ ...cardStyle, gap: '18px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('productDetails.yourRating')}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <button key={star} type="button" onClick={() => setNewRating(star)} onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                    <FaStar size={26} className={star <= (hoverRating || newRating) ? 'text-namke-mustard' : 'text-gray-300'} />
                  </button>
                ))}
                <span style={{ marginLeft: '10px', fontWeight: 600 }}>{t('productDetails.ratingOutOf5', { rating: newRating })}</span>
              </div>
            </div>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.62)' }}>{t('productDetails.yourComment')}</span>
              <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={4} required placeholder={t('productDetails.commentPlaceholder')} style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '20px', padding: '14px 18px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FCFBF5', color: '#051004', resize: 'vertical', outline: 'none' }} />
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="button" onClick={() => { setShowCommentForm(false); setNewComment(''); setNewRating(5); }} disabled={isSubmittingComment} style={{ border: '1px solid rgba(5,16,4,0.14)', background: 'transparent', cursor: 'pointer', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 22px', borderRadius: '999px' }}>{t('productDetails.cancel')}</button>
              <button type="submit" disabled={isSubmittingComment} style={{ flex: 1, border: 'none', cursor: 'pointer', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 22px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', opacity: isSubmittingComment ? 0.6 : 1 }}>
                {isSubmittingComment ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('productDetails.sending')}</> : <><Send size={16} /> {t('productDetails.publishReview')}</>}
              </button>
            </div>
          </form>
        )}

        {commentsLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '20px 0', color: 'rgba(5,16,4,0.66)' }}><Loader2 className="w-5 h-5 animate-spin" /> {t('productDetails.loadingReviews')}</div>
        ) : comments.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: '16px' }}>
            {comments.filter((c) => c && typeof c === 'object').map((comment, idx) => (
              <blockquote key={comment.id ?? `comment-${idx}`} style={{ margin: 0, background: '#FBF9D9', borderRadius: '22px', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, color: '#054625' }}>
                  {'★'.repeat(comment.rating || 0)}{'☆'.repeat(5 - (comment.rating || 0))}
                </span>
                <p style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontSize: '17px', lineHeight: 1.45 }}>« {comment.content} »</p>
                <footer style={{ display: 'flex', alignItems: 'center', gap: '10px', fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.66)' }}>
                  <span style={{ width: '28px', height: '28px', borderRadius: '999px', background: '#72B744', display: 'grid', placeItems: 'center' }}><FaUser size={12} /></span>
                  {comment.user?.email?.split('@')[0] || comment.user_name || t('productDetails.anonymousUser')} · {new Date(comment.created_at || Date.now()).toLocaleDateString(DATE_LOCALES[i18n.language] || 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </footer>
              </blockquote>
            ))}
          </div>
        ) : (
          <div style={{ background: '#FBF9D9', borderRadius: '22px', padding: '40px 28px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: '19px' }}>{t('productDetails.noReviewsYet')}</strong>
            <span style={{ fontSize: '14.5px', color: 'rgba(5,16,4,0.66)', maxWidth: '42ch' }}>{t('productDetails.beFirstToReview')}</span>
          </div>
        )}
      </section>

      {showLoginPopup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(5,16,4,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#FCFBF5', borderRadius: '24px', padding: '28px', maxWidth: '380px', width: '100%', margin: '0 16px' }}>
            <h3 style={{ margin: '0 0 12px', fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '20px' }}>{t('productDetails.loginRequired')}</h3>
            <p style={{ margin: '0 0 20px', fontSize: '14.5px', color: 'rgba(5,16,4,0.78)' }}>{t('productDetails.loginToFavorite')}</p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={closeLoginPopup} style={{ flex: 1, border: '1px solid rgba(5,16,4,0.14)', background: 'transparent', cursor: 'pointer', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 0', borderRadius: '999px' }}>{t('productDetails.cancel')}</button>
              <button onClick={() => { window.location.href = '/login'; }} style={{ flex: 1, border: 'none', cursor: 'pointer', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 0', borderRadius: '999px' }}>{t('productDetails.logInButton')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DetailCard;
